/**
 * HeadquartersLocation
 *
 * Location wrapper for the Headquarters/Dashboard view.
 * Features an elevator-style floor selector for navigating between
 * Lobby (overview), HR (employees), Finance, and Strategy floors.
 */

import React, { useState } from 'react';
import { CampusLocation } from '../../types/spatial';
import LocationWrapper from '../spatial/LocationWrapper';
import { FloorSelector, type FloorId } from '../spatial/FloorSelector';
import { HQFloorContent } from './HQFloorContent';
import { useDynamicEvents } from '@/stores/eventStore';
import type { CompanyState } from '@/types/company';
import type { Product, Employee, HiringProcess } from '@/types/tycoon';
import type { BuildingCapacity } from '@/types/buildings';
import type { EmployeeAllocation } from '@/services/tycoon/employeeAllocationService';

export interface HeadquartersLocationProps {
  /** Company state data */
  companyState: CompanyState;
  /** Company display name */
  companyName?: string;
  /** List of products */
  products: Product[];
  /** List of employees */
  employees: Employee[];
  /** Active hiring processes */
  hiringProcesses: HiringProcess[];
  /** Whether to show fire warning modal */
  showFireWarning?: boolean;
  /** Callback to hire employees */
  onHire?: (role: string, count: number) => void;
  /** Callback to fire employees */
  onFire?: (role: string, count: number) => void;
  /** Callback to toggle fire warning */
  onToggleFireWarning?: (show: boolean) => void;
  /** Building capacity info */
  buildingCapacity?: BuildingCapacity;
  /** Function to check if hiring can proceed */
  checkCapacityForHiring?: (role: string, count: number, hiringProcesses?: HiringProcess[]) => {
    hasCapacity: boolean;
    availableCapacity: number;
    reason?: string;
  };
  /** Employee allocations */
  employeeAllocations?: EmployeeAllocation[];
  /** Callback to update company state */
  onStateUpdate?: (updates: Partial<CompanyState>) => void;
}

/**
 * HeadquartersLocation provides floor-based navigation within the HQ building.
 * Floors: Lobby (G), HR (2), Finance (3), Strategy (4)
 */
export const HeadquartersLocation: React.FC<HeadquartersLocationProps> = ({
  companyState,
  companyName = '',
  products,
  employees,
  hiringProcesses,
  showFireWarning = true,
  onHire = () => {},
  onFire = () => {},
  onToggleFireWarning = () => {},
  buildingCapacity,
  checkCapacityForHiring,
  employeeAllocations = [],
  onStateUpdate,
}) => {
  const [currentFloor, setCurrentFloor] = useState<FloorId>('lobby');
  const { data: { eventHistory } } = useDynamicEvents();

  return (
    <LocationWrapper
      location={CampusLocation.HEADQUARTERS}
      title="Headquarters"
    >
      <div className="flex h-full">
        {/* Floor Selector - Left Side */}
        <FloorSelector
          currentFloor={currentFloor}
          onFloorSelect={setCurrentFloor}
          className="w-48 flex-shrink-0"
        />

        {/* Floor Content - Main Area */}
        <div className="flex-1 overflow-auto">
          <div key={currentFloor} className="animate-floorTransition h-full">
            <HQFloorContent
              currentFloor={currentFloor}
              companyState={companyState}
              companyName={companyName}
              products={products}
              employees={employees}
              hiringProcesses={hiringProcesses}
              showFireWarning={showFireWarning}
              onHire={onHire}
              onFire={onFire}
              onToggleFireWarning={onToggleFireWarning}
              buildingCapacity={buildingCapacity}
              checkCapacityForHiring={checkCapacityForHiring}
              employeeAllocations={employeeAllocations}
              onStateUpdate={onStateUpdate}
              eventHistory={eventHistory}
            />
          </div>
        </div>
      </div>
    </LocationWrapper>
  );
};

export default HeadquartersLocation;
